import Dayjs from "dayjs";

// Custom.
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import { GeneralObject } from "@vocollege/app/dist/global";
import { getContactName } from "@vocollege/app/dist/modules/VoHelpers";
import { orderByField } from "@vocollege/app/dist/modules/VoHelpers";

export const getPageTitle = (data: GeneralObject, defaultTitle: string) => {
  if (data.id === 0) {
    return defaultTitle;
  }
  let output: string[] = [];
  if (data.personal_number && data.personal_number) {
    output.push(data.personal_number);
  } else {
    output.push(getContactName(data));
  }
  output.push(`(${data.id})`);
  return output.join(" ");
};

export const getStringPlaceHolder = () => {
  return "xxxxxxxx";
};

export const getFileName = (parts: string[] = []) => {
  let now = Dayjs();
  let newParts = Array.from(parts);
  newParts.push(now.format("YYYYMMDD"));
  newParts.push(now.format("HHmmss"));
  return newParts.join("_").toLowerCase();
};

export const getAvailableCollegeValues = (data: any) => {
  let values = [
    {
      label: I18n.get.misc.notVoCollege,
      value: "0",
    },
  ];
  if (data && data.allGroups) {
    values = values.concat(
      data.allGroups.map((v: any) => ({
        label: v.title,
        value: v.id,
      }))
    );
  }
  return values;
};

export const getStatisticsData = (data: any = null, groupBy = "educator") => {
  let result: GeneralObject = {};
  let parsedData = JSON.parse(data);
  parsedData.forEach((row: GeneralObject) => {
    if (!result.hasOwnProperty(row.id)) {
      result[row.id] = {
        ...row,
        total: 0,
        items: [],
      };
    }
    result[row.id].items.push(row);
    result[row.id].total += row.total;
  });
  let sortedResult = Object.values(result)
    .sort((a: unknown, b: unknown) => orderByField(a, b, "title"))
    .map((v: GeneralObject) => result[v.id]);
  return sortedResult;
};

export const getStatisticsDataWithChildren = (
  data: any = null,
  groupBy = "educator"
) => {
  if (!data) {
    return [];
  }

  let result: GeneralObject = {
    items: {},
    total: 0,
    itemsPerEducatorType: {},
  };

  let parsedData = JSON.parse(data);

  let id = "id";
  switch (groupBy) {
    case "region":
      id = "local_id";
      break;
  }

  parsedData.forEach((row: GeneralObject) => {
    // Root level.
    if (!result.items.hasOwnProperty(row.id)) {
      result.items[row.id] = {
        ...row,
        id: row.id,
        title: row.title,
        total: 0,
        children: {},
      };
    }
    result.items[row.id].total += row.total;
    result.total += row.total;

    // Level 1.
    if (!result.items[row.id].children.hasOwnProperty(row.local_id)) {
      result.items[row.id].children[row.local_id] = {
        ...row,
        id: row.local_id,
        title: row.local_title,
        total: 0,
        educators: {},
      };
    }

    result.items[row.id].children[row.local_id].total += row.total;

    // Educator.
    if (
      !result.items[row.id].children[row.local_id].educators.hasOwnProperty(
        row.educator_id
      )
    ) {
      result.items[row.id].children[row.local_id].educators[row.educator_id] = {
        ...row,
        id: row.educator_id,
        title: row.educator_title,
        total: 0,
      };
    }

    result.items[row.id].children[row.local_id].educators[
      row.educator_id
    ].total += row.total;

    if ("educator_type" in row) {
      let educatorType = JSON.parse(row.educator_type);
      if (educatorType) {
        educatorType.forEach((type: string) => {
          let key = `${type}`;
          if (!(key in result.itemsPerEducatorType)) {
            result.itemsPerEducatorType[key] = {
              label: I18n.get.group.educatorTypes[key],
              note: "",
              value: 0,
            };
          }
          result.itemsPerEducatorType[key].value += row.total;
        });
      }
    }
  });

  return result;
};

export const getStatisticsDataForTutorCertificates = (data: any = null) => {
  if (!data) {
    return [];
  }

  let result: GeneralObject = {
    items: {},
    total: 0,
    itemsPerEducatorType: {},
  };

  let parsedData = JSON.parse(data);

  parsedData.forEach((row: GeneralObject) => {
    // Root level.
    if (!result.items.hasOwnProperty(row.id)) {
      result.items[row.id] = {
        ...row,
        id: row.id,
        title: row.title,
        total: 0,
        local: {},
      };
    }
    result.items[row.id].total += row.total;
    result.total += row.total;

    // Level 1.
    if (!result.items[row.id].children.hasOwnProperty(row.local_id)) {
      result.items[row.id].local[row.local_id] = {
        ...row,
        id: row.local_id,
        title: row.local_title,
        total: 0,
        educators: {},
      };
    }

    result.items[row.id].children[row.local_id].total += row.total;

    // Educator.
    if (
      !result.items[row.id].children[row.local_id].educators.hasOwnProperty(
        row.educator_id
      )
    ) {
      result.items[row.id].children[row.local_id].educators[row.educator_id] = {
        ...row,
        id: row.educator_id,
        title: row.educator_title,
        total: 0,
      };
    }

    result.items[row.id].children[row.local_id].educators[
      row.educator_id
    ].total += row.total;

    if ("educator_type" in row) {
      let educatorType = JSON.parse(row.educator_type);
      if (educatorType) {
        educatorType.forEach((type: string) => {
          let key = `${type}`;
          if (!(key in result.itemsPerEducatorType)) {
            result.itemsPerEducatorType[key] = {
              label: I18n.get.group.educatorTypes[key],
              note: "",
              value: 0,
            };
          }
          result.itemsPerEducatorType[key].value += row.total;
        });
      }
    }
  });

  return result;
};

export const getStatisticsDataForTutorCertificates2 = (data: any = null) => {
  if (!data) {
    return [];
  }

  let result: GeneralObject = {
    items: {},
    total: 0,
    // itemsPerEducatorType: {},
  };

  let parsedData = JSON.parse(data);

  // console.log("getStatisticsDataForTutorCertificates2 parsedData", parsedData);

  parsedData.forEach((row: GeneralObject) => {
    // Root level.
    if (!result.items.hasOwnProperty(row.id)) {
      result.items[row.id] = {
        ...row,
        id: row.id,
        title: row.title,
        total: 0,
        children: {
          no_local: {
            total: 0,
            children: {},
          },
        },
      };
    }
    result.items[row.id].total += row.total;
    result.total += row.total;

    // console.log("test result", JSON.parse(JSON.stringify(result)));

    // No local college.
    if (
      !row?.local_id &&
      !result.items[row.id].children.no_local.children.hasOwnProperty(
        row.certificate_id
      )
    ) {
      result.items[row.id].children.no_local.children[row.certificate_id] = {
        ...row,
        id: row.certificate_id,
        title: row.title,
        total: 0,
        educators: {
          no_local: {
            total: 0,
            children: {},
          },
        },
      };
      result.items[row.id].children.no_local.total += row.total;
    }

    // With local college.
    else if (!result.items[row.id].children.hasOwnProperty(row.local_id)) {
      result.items[row.id].children[row.local_id] = {
        ...row,
        id: row.local_id,
        title: row.local_title,
        total: 0,
        children: {
          no_educator: {
            total: 0,
            children: {},
          },
        },
      };
      result.items[row.id].children[row.local_id].total += row.total;
    }

    // No educator.
    if (
      row.local_id &&
      !row?.educator_id &&
      !result.items[row.id].children[
        row.local_id
      ].children.no_educator.children.hasOwnProperty(row.certificate_id)
    ) {
      result.items[row.id].children[row.local_id].children.no_educator.children[
        row.certificate_id
      ] = {
        ...row,
        id: row.certificate_id,
        title: row.title,
        total: 0,
      };
      result.items[row.id].children[row.local_id].children.no_educator.total +=
        row.total;
    }

    // With educator.
    else if (
      row.local_id &&
      !result.items[row.id].children[row.local_id].children.hasOwnProperty(
        row.educator_id
      )
    ) {
      result.items[row.id].children[row.local_id].children[row.educator_id] = {
        ...row,
        id: row.educator_id,
        title: row.educator_title,
        total: 0,
      };

      result.items[row.id].children[row.local_id].children[
        row.educator_id
      ].total += row.total;
    }

    // @note This can be used in the future if "educator type" needs to be presented in the statistics.
    //
    // if ("educator_type" in row) {
    //   let educatorType = JSON.parse(row.educator_type);
    //   if (educatorType) {
    //     educatorType.forEach((type: string) => {
    //       let key = `${type}`;
    //       if (!(key in result.itemsPerEducatorType)) {
    //         result.itemsPerEducatorType[key] = {
    //           label: I18n.get.group.educatorTypes[key],
    //           note: "",
    //           value: 0,
    //         };
    //       }
    //       result.itemsPerEducatorType[key].value += row.total;
    //     });
    //   }
    // }

    // const newRow = JSON.parse(JSON.stringify(row));
    // setSteps(row, newRow, result.steps, result.step2);
  });

  // console.log("steps", result.steps);
  // console.log("step2", result.step2);

  // console.log("getStatisticsDataForTutorCertificates2 result", result);

  return result;
};

export const getStatisticsDataForDiplomas = (
  data: any = null,
  groupBy = "educator"
) => {
  if (!data) {
    return [];
  }
  let result: GeneralObject = {
    items: {},
    total: 0,
    itemsPerEducation: {},
    itemsPerEducatorType: {},
  };
  let parsedData = JSON.parse(data);
  let id = "id";
  switch (groupBy) {
    case "region":
      id = "local_id";
      break;
  }
  parsedData.forEach((row: GeneralObject) => {
    // Root level.
    if (!result.items.hasOwnProperty(row.id)) {
      result.items[row.id] = {
        id: row.id,
        title: row.title,
        total: 0,
        children: {},
      };
    }
    result.items[row.id].total += row.total;
    result.total += row.total;

    // Leve 1.
    if (!result.items[row.id].children.hasOwnProperty(row.local_id)) {
      result.items[row.id].children[row.local_id] = {
        id: row.local_id,
        title: row.local_title,
        total: 0,
        educations: {},
      };
    }

    result.items[row.id].children[row.local_id].total += row.total;

    // Education.
    let key = `${row.diploma_type}_${row.education}`;

    if (
      !result.items[row.id].children[row.local_id].educations.hasOwnProperty(
        key
      )
    ) {
      result.items[row.id].children[row.local_id].educations[key] = {
        id: row.diploma_type,
        diploma_type: row.diploma_type,
        education: row.education,
        title: I18n.get.diploma.diplomaTypes[row.diploma_type],
        subtitle:
          row.diploma_type !== "yh" &&
          I18n.get.diploma.education[row.diploma_type][row.education],
        educators: {},
        total: 0,
      };
    }

    result.items[row.id].children[row.local_id].educations[key].total +=
      row.total;

    // Educator.
    if (
      !result.items[row.id].children[row.local_id].educations[
        key
      ].educators.hasOwnProperty(row.educator_id)
    ) {
      result.items[row.id].children[row.local_id].educations[key].educators[
        row.educator_id
      ] = {
        id: row.educator_id,
        title: row.educator_title,
        total: 0,
      };
    }

    result.items[row.id].children[row.local_id].educations[key].educators[
      row.educator_id
    ].total += row.total;

    // Retrieve items per education.
    let key2 = `${row.diploma_type}_${row.education}`;
    if (!(key2 in result.itemsPerEducation)) {
      result.itemsPerEducation[key2] = {
        label: I18n.get.diploma.diplomaTypes[row.diploma_type],
        note:
          row.diploma_type !== "yh" &&
          I18n.get.diploma.education[row.diploma_type][row.education],
        value: 0,
      };
    }
    result.itemsPerEducation[key2].value += row.total;

    // Retrieve items per educator type.
    if ("educator_type" in row) {
      let educatorType = JSON.parse(row.educator_type);
      if (educatorType) {
        educatorType.forEach((type: string) => {
          let key = `${type}`;
          if (!(key in result.itemsPerEducatorType)) {
            result.itemsPerEducatorType[key] = {
              label: I18n.get.group.educatorTypes[key],
              note: "",
              value: 0,
            };
          }
          result.itemsPerEducatorType[key].value += row.total;
        });
      }
    }
  });

  return result;
};

export const groupTutorCertificatesStatisticsDataBySteps = (
  data: any = null
) => {
  if (!data) {
    return [];
  }
  let result: GeneralObject[] = [];

  data.forEach((row: GeneralObject) => {
    let newRow = JSON.parse(JSON.stringify(row));
    delete newRow.items;
    delete newRow.steps;
    delete newRow.step2;
    newRow.total = 0;
    let steps: GeneralObject = {};
    let step2: GeneralObject = {};

    row.items.forEach((rowItem: GeneralObject) => {
      switch (true) {
        case rowItem.steps.length === 1 && rowItem.steps[0] === "1":
          if (!steps.hasOwnProperty("step1")) {
            steps.step1 = 0;
          }
          steps.step1++;
          newRow.total++;
          break;
        case rowItem.steps.length === 1 && rowItem.steps[0] === "2":
          if (!steps.hasOwnProperty("step2")) {
            steps.step2 = 0;
          }
          steps.step2++;
          newRow.total++;
          break;
        case rowItem.steps.length === 1 && rowItem.steps[0] === "3":
          if (!steps.hasOwnProperty("step3")) {
            steps.step3 = 0;
          }
          steps.step3++;
          newRow.total++;
          break;
        case rowItem.steps.indexOf("1") > -1 && rowItem.steps.indexOf("2") > -1:
          if (!steps.hasOwnProperty("step1_2")) {
            steps.step1_2 = 0;
          }
          steps.step1_2++;
          newRow.total++;
          break;
        default:
          break;
      }

      rowItem.step2 &&
        rowItem.step2.forEach((mod: string) => {
          switch (mod) {
            case "step2-mod01":
              if (!step2.hasOwnProperty("step2-mod01")) {
                step2["step2-mod01"] = 0;
              }
              step2["step2-mod01"]++;
              break;
            case "step2-mod02":
              if (!step2.hasOwnProperty("step2-mod02")) {
                step2["step2-mod02"] = 0;
              }
              step2["step2-mod02"]++;
              break;
            case "step2-mod03":
              if (!step2.hasOwnProperty("step2-mod03")) {
                step2["step2-mod03"] = 0;
              }
              step2["step2-mod03"]++;
              break;
            case "step2-mod04":
              if (!step2.hasOwnProperty("step2-mod04")) {
                step2["step2-mod04"] = 0;
              }
              step2["step2-mod04"]++;
              break;
          }
        });
    });

    newRow.steps = Object.keys(steps)
      .sort()
      .map((step: string) => ({
        step,
        total: steps[step],
      }));

    newRow.step2 = Object.keys(step2)
      .sort()
      .map((mod: string) => ({
        mod,
        total: step2[mod],
      }));

    result.push(newRow);
  });
  return result;
};

export const groupTutorCertificatesStatisticsDataBySteps2 = (
  data: any = null
) => {
  // if (!data) {
  //   return [];
  // }

  // console.log("groupTutorCertificatesStatisticsDataBySteps2 data", data);

  // let result: GeneralObject[] = [];

  // Object.keys(data.items).forEach((k: string) => {
  //   const row = data.items[k];
  //   let newRow = JSON.parse(JSON.stringify(row));
  //   delete newRow.items;
  //   delete newRow.steps;
  //   delete newRow.step2;
  //   newRow.total = 0;
  //   let steps: GeneralObject = {};
  //   let step2: GeneralObject = {};

  //   Object.keys(row.children)
  //     // .filter((k: string) => !isNaN(parseInt(k)))
  //     .forEach((k: string) => {
  //       const rowItem = row.children[k];

  //       switch (true) {
  //         case rowItem.steps.length === 1 && rowItem.steps[0] === "1":
  //           if (!steps.hasOwnProperty("step1")) {
  //             steps.step1 = 0;
  //           }
  //           steps.step1++;
  //           newRow.total++;
  //           break;
  //         case rowItem.steps.length === 1 && rowItem.steps[0] === "2":
  //           if (!steps.hasOwnProperty("step2")) {
  //             steps.step2 = 0;
  //           }
  //           steps.step2++;
  //           newRow.total++;
  //           break;
  //         case rowItem.steps.length === 1 && rowItem.steps[0] === "3":
  //           if (!steps.hasOwnProperty("step3")) {
  //             steps.step3 = 0;
  //           }
  //           steps.step3++;
  //           newRow.total++;
  //           break;
  //         case rowItem.steps.indexOf("1") > -1 &&
  //           rowItem.steps.indexOf("2") > -1:
  //           if (!steps.hasOwnProperty("step1_2")) {
  //             steps.step1_2 = 0;
  //           }
  //           steps.step1_2++;
  //           newRow.total++;
  //           break;
  //         default:
  //           break;
  //       }

  //       rowItem.step2 &&
  //         rowItem.step2.forEach((mod: string) => {
  //           switch (mod) {
  //             case "step2-mod01":
  //               if (!step2.hasOwnProperty("step2-mod01")) {
  //                 step2["step2-mod01"] = 0;
  //               }
  //               step2["step2-mod01"]++;
  //               break;
  //             case "step2-mod02":
  //               if (!step2.hasOwnProperty("step2-mod02")) {
  //                 step2["step2-mod02"] = 0;
  //               }
  //               step2["step2-mod02"]++;
  //               break;
  //             case "step2-mod03":
  //               if (!step2.hasOwnProperty("step2-mod03")) {
  //                 step2["step2-mod03"] = 0;
  //               }
  //               step2["step2-mod03"]++;
  //               break;
  //             case "step2-mod04":
  //               if (!step2.hasOwnProperty("step2-mod04")) {
  //                 step2["step2-mod04"] = 0;
  //               }
  //               step2["step2-mod04"]++;
  //               break;
  //           }
  //         });
  //     });

  //   newRow.steps = Object.keys(steps)
  //     .sort()
  //     .map((step: string) => ({
  //       step,
  //       total: steps[step],
  //     }));

  //   newRow.step2 = Object.keys(step2)
  //     .sort()
  //     .map((mod: string) => ({
  //       mod,
  //       total: step2[mod],
  //     }));

  //   result.push(newRow);
  // });

  // console.log("groupTutorCertificatesStatisticsDataBySteps2() result", result);

  // return result;

  // ---

  if (!data) {
    return [];
  }

  // console.log("groupTutorCertificatesStatisticsDataBySteps2() data", data);

  let result: GeneralObject[] = [];

  Object.keys(data.items)
    .filter((k: string) => !isNaN(parseInt(k)))
    .forEach((k: string) => {
      const row = data.items[k];
      let newRow = JSON.parse(JSON.stringify(row));

      delete newRow.items;
      delete newRow.steps;
      delete newRow.step2;
      newRow.total = 0;

      // Build steps.
      let steps: GeneralObject = {};
      let step2: GeneralObject = {};
      // setSteps(row, newRow, steps, step2);

      // Count total for certificates without Local College.
      Object.keys(row.children)
        .filter((v: any) => isNaN(parseInt(v)))
        .forEach((v: any) => {
          newRow.total += row.children[v].total;

          // Retrieve steps.
          Object.keys(row.children[v].children).map((a: any) => {
            setSteps(newRow.children[v].children[a], newRow, steps, step2);
          });
        });

      // Certificates with Local College.
      Object.keys(row.children)
        .filter((v: any) => !isNaN(parseInt(v)))
        .forEach((v: any) => {
          // Reset the "local college" counter.
          newRow.children[v].total = 0;

          // Retrieve steps.
          setSteps(newRow.children[v], newRow, steps, step2);

          // Sum total for certificates without Educator.
          Object.keys(row.children[v].children)
            .filter((v2: any) => isNaN(parseInt(v2)))
            .forEach((v2: any) => {
              newRow.total += row.children[v].children[v2].total;
              newRow.children[v].total += row.children[v].children[v2].total;

              // Retrieve steps.
              Object.keys(row.children[v].children[v2].children).map(
                (a: any) => {
                  setSteps(
                    newRow.children[v].children[v2].children[a],
                    newRow,
                    steps,
                    step2
                  );
                }
              );
            });

          // Count total for certificates with Educator.
          Object.keys(row.children[v].children)
            .filter((v2: any) => !isNaN(parseInt(v2)))
            .forEach((v2: any) => {
              newRow.total += 1;
              newRow.children[v].total += 1;
            });
        });

      // - sortera i bokstavsordning

      newRow.steps = Object.keys(steps)
        .sort()
        .map((step: string) => ({
          step,
          total: steps[step],
        }));
      newRow.step2 = Object.keys(step2)
        .sort()
        .map((mod: string) => ({
          mod,
          total: step2[mod],
        }));

      result.push(newRow);
    });

  return result;
};

export const setSteps = (
  item: GeneralObject,
  parent: GeneralObject = {},
  steps: GeneralObject = {},
  step2: GeneralObject = {}
) => {
  switch (true) {
    case item.steps.length === 1 && item.steps[0] === "1":
      if (!steps.hasOwnProperty("step1")) {
        steps.step1 = 0;
      }
      steps.step1++;
      // parent.total++;
      break;
    case item.steps.length === 1 && item.steps[0] === "2":
      if (!steps.hasOwnProperty("step2")) {
        steps.step2 = 0;
      }
      steps.step2++;
      // parent.total++;
      break;
    case item.steps.length === 1 && item.steps[0] === "3":
      if (!steps.hasOwnProperty("step3")) {
        steps.step3 = 0;
      }
      steps.step3++;
      // parent.total++;
      break;
    case item.steps.indexOf("1") > -1 && item.steps.indexOf("2") > -1:
      if (!steps.hasOwnProperty("step1_2")) {
        steps.step1_2 = 0;
      }
      steps.step1_2++;
      // parent.total++;
      break;
    default:
      break;
  }

  item.step2 &&
    item.step2.forEach((mod: string) => {
      switch (mod) {
        case "step2-mod01":
          if (!step2.hasOwnProperty("step2-mod01")) {
            step2["step2-mod01"] = 0;
          }
          step2["step2-mod01"]++;
          break;
        case "step2-mod02":
          if (!step2.hasOwnProperty("step2-mod02")) {
            step2["step2-mod02"] = 0;
          }
          step2["step2-mod02"]++;
          break;
        case "step2-mod03":
          if (!step2.hasOwnProperty("step2-mod03")) {
            step2["step2-mod03"] = 0;
          }
          step2["step2-mod03"]++;
          break;
        case "step2-mod04":
          if (!step2.hasOwnProperty("step2-mod04")) {
            step2["step2-mod04"] = 0;
          }
          step2["step2-mod04"]++;
          break;
      }
    });
};

export const getStatisticsTotal = (data: any = null) => {
  if (!data) return 0;
  return data.reduce((acc: any, v: GeneralObject) => {
    return acc + v.total;
  }, 0);
};

export const getTutorCertificateModules = (step: number) => {
  return I18n.get.tutorCertificate.modules.filter(
    (module: GeneralObject) => module.steps.indexOf(step) > -1
  );
};

export const getTutorCertificateStepTotal = (
  data: any = null,
  step?: string
) => {
  if (!data || !step || step === "") {
    return 0;
  }
  return data
    .flatMap((v: GeneralObject) => v.steps)
    .filter((v: GeneralObject) => v.step === step)
    .reduce((acc: any, v: GeneralObject) => acc + v.total, 0);
};

export const getTutorCertificatesStep2ModTotal = (
  data: any = null,
  mod?: string
) => {
  if (!data || !mod || mod === "") {
    return 0;
  }
  let result = 0;
  data
    .filter((v: GeneralObject | null) => Boolean(v))
    .forEach((v: GeneralObject) => {
      let item = v.step2.find((v: GeneralObject) => v.mod === mod);
      if (item) {
        result += item.total;
      }
    });

  return result;
};

export const getCsvHeaders = (data: any = null) => {
  if (!data) {
    return [];
  }
  return [
    { label: "", key: "description" },
    { label: I18n.get.vocertStatistics.total, key: "value" },
  ];
};

export const getCsvData = (data: any = null) => {
  if (!data) {
    return [];
  }
  const result: GeneralObject[] = [];
  data.forEach((v: GeneralObject, i: number) => {
    result.push({ description: v.title, total: v.total });
    if (v?.items && v.items.length > 0) {
      v.items.forEach((v: GeneralObject, i: number) => {
        let description = `    ${
          I18n.get.diploma.diplomaTypes[v.diploma_type]
        }`;
        if (v?.diploma_type) {
          description =
            description +
            (v.diploma_type !== "yh" &&
              `\n    ${
                I18n.get.diploma.education[v.diploma_type][v.education]
              }`);
        }
        result.push({
          description,
          value: v.total,
        });
      });
    }
  });

  return result;
};

export const getGroupTitle = (item: any) => {
  if (!item) {
    return "";
  }
  const title: string[] = [];
  title.push(item.title);
  if (item.parentGroups && item.parentGroups.length > 0) {
    let parentGroupsPart: string[] = [];
    item.parentGroups.forEach((v: GeneralObject) => {
      parentGroupsPart.push(v.title);
    });
    title.push(parentGroupsPart.join(", "));
  }
  return title.join(" | ");
};

export const getGroupTitleValue = (item: any) => {
  if (!item) {
    return "";
  }
  const title: string[] = [item.title];
  if (item.status === 0) {
    title.push(I18n.get.status.unpublished.toLowerCase());
  }
  return title.join(" - ");
};
